import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Wifi, Zap, Phone, Menu, X, ArrowRight, ChevronDown, User, FileText } from "lucide-react";
import { COMPANY_INFO } from "../data/constants";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { href: "#inicio", label: "Inicio" },
    { href: "#paquetes", label: "Paquetes" },
    { href: "/turbo", label: "Turbo", highlight: true, isRoute: true },
    { href: "#cobertura", label: "Cobertura" },
    { href: "/blog", label: "Blog", isRoute: true },
    { href: "#nosotros", label: "Nosotros" },
    { href: "/contacto", label: "Contacto", isRoute: true },
  ];

  const accountLinks = [
    { href: "https://portal.flowing.lat", label: "Portal Clientes", icon: User },
    { href: "https://cfdi.flowing.lat", label: "Facturación Electrónica", icon: FileText },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "navbar-scrolled py-3" : "bg-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#inicio" className="flex items-center gap-2" data-testid="logo">
            <img 
              src="/logo-flowing.png" 
              alt="Flowing - Internet por Fibra Óptica en Tacámbaro" 
              className={`h-14 md:h-20 w-auto ${scrolled ? "" : "brightness-0 invert"}`}
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-medium transition-colors hover:text-orange-500 ${
                    scrolled ? "text-gray-700" : "text-white/90"
                  } ${link.highlight ? "text-cyan-400 hover:text-cyan-300 flex items-center gap-1" : ""}`}
                >
                  {link.highlight && <Zap className="w-4 h-4" />}
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className={`font-medium transition-colors hover:text-orange-500 ${
                    scrolled ? "text-gray-700" : "text-white/90"
                  }`}
                >
                  {link.label}
                </a>
              )
            ))}

            {/* Mi Cuenta Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                className={`flex items-center gap-1 font-medium transition-colors hover:text-orange-500 ${
                  scrolled ? "text-gray-700" : "text-white/90"
                }`}
                data-testid="account-dropdown-btn"
              >
                Mi Cuenta
                <ChevronDown className={`w-4 h-4 transition-transform ${accountMenuOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {accountMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                  >
                    {accountLinks.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setAccountMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                        data-testid={`account-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <link.icon className="w-5 h-5" />
                        <span className="font-medium">{link.label}</span>
                        <ArrowRight className="w-4 h-4 ml-auto opacity-50" />
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={COMPANY_INFO.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-500/30 transition-all"
              data-testid="whatsapp-cta"
            >
              <Phone className="w-4 h-4" />
              <span>{COMPANY_INFO.phone}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className={scrolled ? "text-gray-900" : "text-white"} />
            ) : (
              <Menu className={scrolled ? "text-gray-900" : "text-white"} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4 bg-white rounded-2xl shadow-xl p-6"
            >
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  link.isRoute ? (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-gray-700 font-medium py-2 hover:text-orange-500"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-gray-700 font-medium py-2 hover:text-orange-500"
                    >
                      {link.label}
                    </a>
                  )
                ))}

                {/* Mi Cuenta section in mobile */}
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <p className="text-sm text-gray-500 font-semibold mb-3">Mi Cuenta</p>
                  {accountLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 text-gray-700 font-medium py-2 hover:text-orange-500"
                    >
                      <link.icon className="w-5 h-5" />
                      {link.label}
                      <ArrowRight className="w-4 h-4 ml-auto opacity-50" />
                    </a>
                  ))}
                </div>

                <a
                  href={COMPANY_INFO.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-full font-semibold mt-2"
                >
                  <Phone className="w-4 h-4" />
                  <span>Contactar</span>
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};
